import React, { forwardRef, type ReactNode, Children } from "react";

import { cn } from "src/shadcn/lib/utils";
import {
  Accordion as PrimitiveAccordion,
  AccordionItem as PrimitiveAccordionItem,
  AccordionTrigger as PrimitiveAccordionTrigger,
  AccordionContent as PrimitiveAccordionContent,
} from "src/primitives/Accordion";

type AccordionRootProps = React.ComponentProps<typeof PrimitiveAccordion>;

export interface AccordionProps extends Omit<
  AccordionRootProps,
  "children" | "type"
> {
  /** Whether one or many items can be opened at the same time. */
  type?: "single" | "multiple";
  /** Accordion.Item children. */
  children?: ReactNode;
}

interface AccordionItemProps extends Omit<
  React.ComponentProps<typeof PrimitiveAccordionItem>,
  "title"
> {
  /** Header content displayed in the trigger. */
  title: ReactNode;
  /** Content rendered when the item is expanded. */
  children?: ReactNode;
}

const AccordionItemComponent = forwardRef<HTMLDivElement, AccordionItemProps>(
  ({ title, children, className, value, ...otherProps }, ref) => (
    <PrimitiveAccordionItem
      ref={ref}
      value={value}
      className={cn(className)}
      {...otherProps}
    >
      <PrimitiveAccordionTrigger>{title}</PrimitiveAccordionTrigger>
      <PrimitiveAccordionContent>{children}</PrimitiveAccordionContent>
    </PrimitiveAccordionItem>
  )
);

AccordionItemComponent.displayName = "Accordion.Item";

const Accordion = forwardRef<HTMLDivElement, AccordionProps>(
  ({ className, children, type = "single", ...otherProps }, ref) => {
    // Auto-generate value props for items that don't have one
    const enhancedChildren = Children.map(children, (child, index) => {
      if (!React.isValidElement<AccordionItemProps>(child)) return child;
      const childValue = child.props.value ?? `item-${index}`;
      return React.cloneElement(child, { value: childValue });
    });

    return (
      <PrimitiveAccordion
        ref={ref}
        type={type}
        className={cn(className)}
        {...(otherProps as Record<string, unknown>)}
      >
        {enhancedChildren}
      </PrimitiveAccordion>
    );
  }
) as React.ForwardRefExoticComponent<
  AccordionProps & React.RefAttributes<HTMLDivElement>
> & {
  Item: typeof AccordionItemComponent;
};

Accordion.displayName = "Accordion";
Accordion.Item = AccordionItemComponent;

export { Accordion };
